import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiClock, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────────────────────────────── */

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const cardStyle = { marginBottom: 24 };

const sectionTitle = {
  fontWeight: 600,
  fontSize: '0.95rem',
  marginBottom: 18,
  paddingBottom: 10,
  borderBottom: '1px solid #eee',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

/* ─────────────────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────────────────── */

export default function AdminPaymentTerms() {
  const [terms, setTerms]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  // Form state
  const [termText, setTermText]     = useState('');
  const [threshold, setThreshold]   = useState('');
  const [minAmount, setMinAmount]   = useState('');   // order total ≤ this → 100% required
  const [flatAmount, setFlatAmount] = useState('');   // floor rupee amount (whichever is higher)

  /* ── Fetch all terms on mount ── */
  const fetchTerms = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/payment-terms');
      setTerms(data.terms || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load payment terms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTerms(); }, [fetchTerms]);

  const activeTerm  = terms[0] || null;
  const historyTerms = terms.slice(1);

  /* ── Save new term ── */
  const handleSave = async () => {
    if (!termText.trim()) {
      toast.error('Please write the term text.');
      return;
    }
    const parsed = parseFloat(threshold);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast.error('Threshold must be a number between 0 and 100.');
      return;
    }

    try {
      setSaving(true);
      await API.post('/payment-terms', {
        termText: termText.trim(),
        threshold: parsed,
        minAmount:  minAmount  !== '' ? parseFloat(minAmount)  : null,
        flatAmount: flatAmount !== '' ? parseFloat(flatAmount) : null,
      });
      toast.success('Payment term saved and is now active.');
      setTermText('');
      setThreshold('');
      setMinAmount('');
      setFlatAmount('');
      await fetchTerms();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save term.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Styles ── */
  const labelStyle  = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inputStyle  = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
  };
  const textareaStyle = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: 90,
    fontFamily: 'inherit',
    lineHeight: 1.6,
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 16px' }}>
      <h2 style={{ fontWeight: 700, fontSize: '1.4rem', marginBottom: 4 }}>Payment Terms</h2>
      <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: 28 }}>
        Define the minimum payment required before production can start.
        Each saved term becomes the active policy immediately and is logged in history.
      </p>

      {/* ── Active term display ── */}
      <div className="crm-card" style={cardStyle}>
        <div style={sectionTitle}>
          <FiCheckCircle size={16} color="#16a34a" />
          Current active term
        </div>

        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
        ) : activeTerm ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Threshold badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{
                background: '#dcfce7', color: '#166534',
                fontWeight: 700, fontSize: 13, padding: '4px 12px', borderRadius: 20,
              }}>
                Minimum {activeTerm.threshold}% payment required
              </span>
              {activeTerm.flatAmount != null && (
                <span style={{
                  background: '#dbeafe', color: '#1d4ed8',
                  fontWeight: 700, fontSize: 13, padding: '4px 12px', borderRadius: 20,
                }}>
                  Or ₹{Number(activeTerm.flatAmount).toLocaleString('en-IN')} flat — whichever is higher
                </span>
              )}
              {activeTerm.minAmount != null && (
                <span style={{
                  background: '#fef3c7', color: '#92400e',
                  fontWeight: 700, fontSize: 13, padding: '4px 12px', borderRadius: 20,
                }}>
                  Orders ≤ ₹{Number(activeTerm.minAmount).toLocaleString('en-IN')} → full payment
                </span>
              )}
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                Effective from {fmtDate(activeTerm.createdAt)}
              </span>
            </div>
            {/* Term text */}
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb',
              borderRadius: 8, padding: '12px 14px',
              fontSize: 14, color: '#374151', lineHeight: 1.65,
            }}>
              {activeTerm.termText}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#d97706', fontSize: 13 }}>
            <FiAlertCircle size={15} />
            No payment term defined yet. Production can start on any order until a term is set.
          </div>
        )}
      </div>

      {/* ── Write new term ── */}
      <div className="crm-card" style={cardStyle}>
        <div style={sectionTitle}>
          <FiFileText size={16} />
          Write new term
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Minimum payment threshold (%)</label>
          <input
            className="form-control"
            type="number"
            min="0"
            max="100"
            step="0.01"
            placeholder="e.g. 50"
            value={threshold}
            onChange={e => setThreshold(e.target.value)}
            style={{ ...inputStyle, maxWidth: 180 }}
          />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            Enter 0–100. Example: 50 means at least 50% must be received before production starts.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          {/* Min amount — small order full-payment rule */}
          <div>
            <label style={labelStyle}>Small order threshold (₹)</label>
            <input
              className="form-control"
              type="number"
              min="0"
              step="1"
              placeholder="e.g. 1000"
              value={minAmount}
              onChange={e => setMinAmount(e.target.value)}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              If order total is ≤ this amount, <strong>100% full payment</strong> is required.
              Leave blank to skip.
            </div>
          </div>

          {/* Flat amount floor */}
          <div>
            <label style={labelStyle}>Flat minimum amount (₹)</label>
            <input
              className="form-control"
              type="number"
              min="0"
              step="1"
              placeholder="e.g. 1000"
              value={flatAmount}
              onChange={e => setFlatAmount(e.target.value)}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              Customer must pay <strong>whichever is higher</strong> — this amount or the % above.
              Leave blank to use % only.
            </div>
          </div>
        </div>

        {/* Live preview */}
        {(threshold || minAmount || flatAmount) && (
          <div style={{
            marginBottom: 16, padding: '10px 12px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 8, fontSize: 12.5, color: '#1e40af', lineHeight: 1.6,
          }}>
            <strong>Preview:</strong>{' '}
            {minAmount && parseFloat(minAmount) > 0
              ? `Orders of ₹${parseFloat(minAmount).toLocaleString('en-IN')} or below → full payment required. `
              : ''}
            {threshold
              ? `For larger orders: customer must pay ${
                  flatAmount && parseFloat(flatAmount) > 0
                    ? `₹${parseFloat(flatAmount).toLocaleString('en-IN')} or ${threshold}% — whichever is higher`
                    : `at least ${threshold}%`
                }.`
              : ''}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Term description</label>
          <textarea
            className="form-control"
            placeholder="e.g. Payment must be equal to or greater than 50% of the total order amount before production begins. No exceptions without written approval from management."
            value={termText}
            onChange={e => setTermText(e.target.value)}
            style={textareaStyle}
          />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            This text will appear in history and can be referenced for disputes.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="crm-btn crm-btn--primary"
            onClick={handleSave}
            disabled={saving}
            style={{ opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : 'Save & make active'}
          </button>
        </div>
      </div>

      {/* ── History ── */}
      {historyTerms.length > 0 && (
        <div className="crm-card" style={cardStyle}>
          <div style={sectionTitle}>
            <FiClock size={16} />
            History
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {historyTerms.map((term, idx) => (
              <div key={term.id} style={{
                border: '1px solid #f3f4f6',
                borderRadius: 8,
                padding: '12px 14px',
                background: '#fafafa',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{
                    background: '#f3f4f6', color: '#6b7280',
                    fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 20,
                  }}>
                    {term.threshold}% threshold
                  </span>
                  {term.flatAmount != null && (
                    <span style={{
                      background: '#eff6ff', color: '#3b82f6',
                      fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 20,
                    }}>
                      ₹{Number(term.flatAmount).toLocaleString('en-IN')} flat floor
                    </span>
                  )}
                  {term.minAmount != null && (
                    <span style={{
                      background: '#fefce8', color: '#a16207',
                      fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 20,
                    }}>
                      ≤₹{Number(term.minAmount).toLocaleString('en-IN')} → 100%
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    {fmtDate(term.createdAt)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                  {term.termText}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && terms.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 40 }}>
          No terms saved yet. Write your first term above.
        </div>
      )}
    </div>
  );
}